<!-- Header -->
<div class="mb-6">
  <h2 class="text-xl font-bold text-gray-900">Settings</h2>
  <p class="text-sm text-gray-500 mt-0.5">
    {{ isAdmin() ? 'Update your account password.' : 'Manage your profile, contact details, and password.' }}
  </p>
</div>

<div class="space-y-6 max-w-3xl">

  <!-- Profile (default users only) -->
  @if (!isAdmin()) {
    <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div class="flex items-center gap-2 mb-4">
        <span class="w-1 h-5 bg-blue-500 rounded"></span>
        <h3 class="text-sm font-semibold text-gray-800">My Profile</h3>
      </div>

      @if (isLoadingProfile) {
        <div class="flex items-center gap-2 text-sm text-gray-500">
          <app-spinner size="xs"></app-spinner> Loading profile…
        </div>
      } @else if (profileError) {
        <div class="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{{ profileError }}</div>
      } @else if (profile) {
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-xs text-gray-500 mb-0.5">Full name</p>
            <p class="font-medium text-gray-900">{{ profile.fullName }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-0.5">Username</p>
            <p class="font-medium text-gray-900">{{ profile.username }}</p>
          </div>
          <div>
            <div class="flex items-center justify-between gap-2 mb-0.5">
              <p class="text-xs text-gray-500">NIC</p>
              <button type="button" (click)="openNicRequest()"
                      class="text-xs font-medium text-blue-600 hover:text-blue-700">
                Request change
              </button>
            </div>
            <p class="font-medium text-gray-900">{{ profile.nic || '—' }}</p>
            @if (hasPendingForField('NIC')) {
              <p class="text-xs text-amber-700 mt-1">A NIC change request is pending review.</p>
            }
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-0.5">Member since</p>
            <p class="font-medium text-gray-900">{{ profile.joinedDate ? (profile.joinedDate | date:'mediumDate') : '—' }}</p>
          </div>
          <div class="md:col-span-2">
            <p class="text-xs text-gray-500 mb-0.5">Address</p>
            <p class="font-medium text-gray-900">{{ profile.address || '—' }}</p>
          </div>
          <div class="md:col-span-2">
            <p class="text-xs text-gray-500 mb-1">My committees</p>
            <div class="flex flex-wrap gap-1.5">
              @for (bu of profile.businessUnits; track bu.id) {
                <span class="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                  {{ bu.name }}
                </span>
              }
              @if (profile.businessUnits.length === 0) {
                <span class="text-xs text-gray-400">No committees assigned.</span>
              }
            </div>
          </div>
        </div>
      }
    </section>

    <!-- Contact -->
    <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div class="flex items-center gap-2 mb-4">
        <span class="w-1 h-5 bg-blue-500 rounded"></span>
        <h3 class="text-sm font-semibold text-gray-800">Contact Details</h3>
      </div>
      <p class="text-xs text-gray-500 mb-4">
        Changes to Email and Phone are submitted to an admin for review. You'll be notified once they're approved.
      </p>

      <form [formGroup]="contactForm" (ngSubmit)="saveContact()" class="space-y-4">
        @if (contactMessage) {
          <div class="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-700">{{ contactMessage }}</div>
        }
        @if (contactError) {
          <div class="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{{ contactError }}</div>
        }

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Phone <span class="text-red-500">*</span></label>
            <input type="tel" formControlName="phone"
                   class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            @if (contactForm.get('phone')?.invalid && contactForm.get('phone')?.touched) {
              <p class="text-xs text-red-600 mt-1">Phone is required.</p>
            }
            @if (hasPendingForField('Phone')) {
              <p class="text-xs text-amber-700 mt-1">A phone change request is pending review.</p>
            }
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input type="email" formControlName="email"
                   class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            @if (contactForm.get('email')?.invalid && contactForm.get('email')?.touched) {
              <p class="text-xs text-red-600 mt-1">Invalid email format.</p>
            }
            @if (hasPendingForField('Email')) {
              <p class="text-xs text-amber-700 mt-1">An email change request is pending review.</p>
            }
          </div>
        </div>

        <div class="flex justify-end">
          <button type="submit" [disabled]="isSavingContact"
                  class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
            <app-spinner *ngIf="isSavingContact" size="xs" colour="white"></app-spinner>
            {{ isSavingContact ? 'Submitting…' : 'Submit Changes' }}
          </button>
        </div>
      </form>
    </section>

    <!-- My change requests -->
    <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <span class="w-1 h-5 bg-blue-500 rounded"></span>
          <h3 class="text-sm font-semibold text-gray-800">My Change Requests</h3>
        </div>
        @if (isLoadingRequests) {
          <app-spinner size="xs"></app-spinner>
        }
      </div>

      @if (myRequests.length === 0 && !isLoadingRequests) {
        <p class="text-xs text-gray-500">No change requests yet.</p>
      }

      @if (myRequests.length > 0) {
        <div class="space-y-2">
          @for (r of myRequests; track r.requestId) {
            <div class="border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-semibold text-gray-700">{{ r.fieldName }}</span>
                  <span class="px-1.5 py-0.5 text-[10px] font-medium rounded-full" [class]="statusBadgeClass(r.status)">
                    {{ r.status }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 mt-0.5 truncate">
                  {{ r.oldValue || '—' }} → <span class="font-medium text-gray-700">{{ r.newValue }}</span>
                </p>
                @if (r.status === 'Rejected' && r.reviewerNotes) {
                  <p class="text-xs text-red-600 mt-1">Reason: {{ r.reviewerNotes }}</p>
                }
                <p class="text-[10px] text-gray-400 mt-0.5">Submitted {{ r.createdDate | date:'short' }}</p>
              </div>
              @if (r.status === 'Pending') {
                <button (click)="cancelRequest(r)"
                        class="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors">
                  Cancel
                </button>
              }
            </div>
          }
        </div>
      }
    </section>
  }

  <!-- Change password -->
  <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
    <div class="flex items-center gap-2 mb-4">
      <span class="w-1 h-5 bg-blue-500 rounded"></span>
      <h3 class="text-sm font-semibold text-gray-800">Change Password</h3>
    </div>

    <form [formGroup]="passwordForm" (ngSubmit)="savePassword()" class="space-y-4">
      @if (passwordMessage) {
        <div class="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-700">{{ passwordMessage }}</div>
      }
      @if (passwordError) {
        <div class="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{{ passwordError }}</div>
      }

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Current password <span class="text-red-500">*</span></label>
          <input type="password" formControlName="currentPassword"
                 class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">New password <span class="text-red-500">*</span></label>
          <input type="password" formControlName="newPassword"
                 class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Confirm new password <span class="text-red-500">*</span></label>
          <input type="password" formControlName="confirmPassword"
                 class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>

      <div class="flex justify-end">
        <button type="submit" [disabled]="isSavingPassword"
                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
          <app-spinner *ngIf="isSavingPassword" size="xs" colour="white"></app-spinner>
          {{ isSavingPassword ? 'Saving…' : 'Update Password' }}
        </button>
      </div>
    </form>
  </section>

</div>

<!-- NIC request modal -->
@if (showNicRequest) {
  <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
    <div class="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-5 sm:p-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:pb-6 max-h-[92dvh] overflow-y-auto overscroll-contain">
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Request NIC change</h3>
      <p class="text-xs text-gray-500 mb-4">
        An admin will review and apply this change to your member record.
      </p>

      <div class="space-y-3">
        <div>
          <p class="text-xs text-gray-500">Current NIC</p>
          <p class="font-mono text-sm text-gray-700">{{ profile?.nic || '—' }}</p>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">New NIC <span class="text-red-500">*</span></label>
          <input type="text" [(ngModel)]="nicValue"
                 class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <textarea [(ngModel)]="nicReason" rows="2"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. New national ID issued"></textarea>
        </div>
      </div>

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
        <button (click)="closeNicRequest()" [disabled]="isSubmittingNic"
                class="w-full sm:w-auto max-md:tap-target px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          Cancel
        </button>
        <button (click)="submitNicRequest()" [disabled]="isSubmittingNic"
                class="w-full sm:w-auto max-md:tap-target inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
          <app-spinner *ngIf="isSubmittingNic" size="xs" colour="white"></app-spinner>
          Submit Request
        </button>
      </div>
    </div>
  </div>
}
